import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
  getStraightPath,
  useInternalNode,
} from "@xyflow/react"
import { getEdgeLabel } from "@features/builder/helpers/edges"
import { AppNode } from "@features/builder/graph/types"
import { getEdgeParams } from "./getEdgeParams"
import { memo } from "react"

const MovableEdge = memo((props: EdgeProps) => {
  const id = props.id as string
  const source = props.source as string
  const target = props.target as string
  const { markerEnd, selected } = props
  const sourceNode = useInternalNode<AppNode>(source)
  const targetNode = useInternalNode<AppNode>(target)
  const label = props.label || getEdgeLabel({ source: sourceNode, target: targetNode })

  if (!sourceNode || !targetNode) {
    return null
  }

  const { sx, sy, tx, ty } = getEdgeParams(sourceNode, targetNode)

  const [edgePath, labelX, labelY] = getStraightPath({
    sourceX: sx,
    sourceY: sy,
    targetX: tx,
    targetY: ty,
  })

  let markerColor = "black"
  if (markerEnd) {
    markerColor = Object.fromEntries(new URLSearchParams(markerEnd))["url('#1__color"] ?? "black"
  }

  // Filter out React Flow specific props that shouldn't go to DOM
  const {
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    sourceHandleId,
    targetHandleId,
    pathOptions,
    selectable,
    deletable,
    ...domProps
  } = props

  return (
    <>
      <svg style={{ position: "absolute", top: 0, left: 0 }}>
        <defs>
          <marker
            className="react-flow__arrowhead"
            id={`${id}-marker-normal`}
            markerWidth="15"
            markerHeight="15"
            viewBox="-10 -10 20 20"
            markerUnits="strokeWidth"
            orient="auto-start-reverse"
            refX="0"
            refY="0"
          >
            <polyline
              style={{
                stroke: markerColor,
                fill: markerColor,
                strokeWidth: 1,
              }}
              strokeLinecap="round"
              strokeLinejoin="round"
              points="-5,-4 0,0 -5,4 -5,-4"
            />
          </marker>
          <marker
            className="react-flow__arrowhead"
            id={`${id}-marker-red`}
            markerWidth="15"
            markerHeight="15"
            viewBox="-10 -10 20 20"
            markerUnits="strokeWidth"
            orient="auto-start-reverse"
            refX="0"
            refY="0"
          >
            <polyline
              style={{
                stroke: "rgb(214, 0, 42, 0.5)",
                fill: "rgb(214, 0, 42, 1.0)",
                strokeWidth: 1,
              }}
              strokeLinecap="round"
              strokeLinejoin="round"
              points="-5,-4 0,0 -5,4 -5,-4"
            />
          </marker>
        </defs>
      </svg>
      <BaseEdge
        {...domProps}
        path={edgePath}
        id={id}
        target={target}
        markerEnd={label ? `url(#${id}-marker-red)` : `url(#${id}-marker-normal)`}
      />
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            }}
            className="edge-label-renderer__custom-edge nodrag nopan"
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  )
})

MovableEdge.displayName = "MovableEdge"

export default MovableEdge
